import type { ReactElement } from 'react';
import {
  cloneElement,
  createContext,
  forwardRef,
  useContext,
  useImperativeHandle,
  useState
} from 'react';
import type { WidgetSettings } from './widget-settings.interface';

export const SettingsContext = createContext({});

export type SettingsProviderProps = {
  children: ReactElement;
  initialSettings: WidgetSettings;
};

export type SettingsProviderAPI = {
  setSettings: (settings: Partial<WidgetSettings>) => void;
};

export const SettingsProvider = forwardRef<
  SettingsProviderAPI,
  SettingsProviderProps
>(function SettingsProvider({ children, initialSettings }, forwardingAPIRef) {
  const [settings, setSettings] = useState<WidgetSettings>(initialSettings);

  useImperativeHandle(
    forwardingAPIRef,
    () => ({
      setSettings: (nextSettings: Partial<WidgetSettings>) =>
        setSettings((prevSettings) => ({ ...prevSettings, ...nextSettings }))
    }),
    []
  );

  return (
    <SettingsContext.Provider value={settings}>
      {cloneElement(children, { ...children.props, ...settings })}
    </SettingsContext.Provider>
  );
});

export function useSettings<TSettings = WidgetSettings>() {
  const context = useContext(SettingsContext);

  if (context === null) {
    throw new Error(`useSettings should be used within SettingsContext`);
  }

  return context as TSettings;
}
